import { Injectable } from '@angular/core';
import { TransactionService } from './transaction.service';
import {
  Survey,
  Option,
  Reply,
  Check,
  CheckTypes,
  SurveyLevelAnswer,
  ReplyItem
} from '../../classes';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { ToggleQueue } from './toggle-queue';
import { get, flatten, keys, merge } from 'lodash';
import { AuthService } from '../auth/auth.service';
import { AngularFireDatabase } from '@angular/fire/database';
import {
  map,
  first,
  tap,
  switchMap,
  startWith,
  catchError,
  last,
  mergeMap,
  timeout
} from 'rxjs/operators';
import { ToggleEventTypes, ToggleEvent } from './toggle-events';
import { takeWhileInclusive } from 'rxjs-take-while-inclusive';
import * as _firebase from 'firebase/app';
const firebase = _firebase;

@Injectable({
  providedIn: 'root'
})
export class ReplyService {
  toggleEvent = new Subject();
  tracker: Reply;
  data:ReplyItem;
  get onChange(): Observable<any> {
    return this.tracker.onContentUpdate;
  }
  private queue = new ToggleQueue();
  constructor(
    private transaction: TransactionService,
    private auth: AuthService,
    private db: AngularFireDatabase
  ) {
    this.tracker = new Reply();
  }
  get isChanged() {
    return this.tracker.getChildTracker(this.auth.as).isChanged;
  }

  toggle(option: Option, value: any, as: string = this.auth.as): Promise<any> {
    if (value === null) {
      return this.uncheck(option, as).toPromise();
    } else {
      return Promise.all([
        this.handleMaxCheckedOptionCnt(option, as).toPromise(),
        this.check(option, value, as).toPromise()
      ]);
    }
  }
  watch(survey: Survey, as: string = this.auth.as): Observable<any> {
    return forkJoin(
      flatten(
        survey.questions.map(q =>
          q.options
            .filter(o => typeof o.quota === 'number' && o.quota > 0)
            .map(o => {
              return this.db
                .object(this.transaction.getCheckRef(o, as))
                .valueChanges()
                .pipe(tap(v => this.tracker.set([as, q.id, o.id], v)));
            })
        )
      )
    );
  }

  getLatest(
    survey_id: string,
    as: string = this.auth.as,
    shouldMatch: Partial<SurveyLevelAnswer> = undefined
  ): Observable<SurveyLevelAnswer> {
    return this.db
      .object(`replyMap/${survey_id}/${as}`)
      .valueChanges()
      .pipe(
        first(),
        map(map => keys(map).pop()),
        mergeMap(latest_reply_id => {
          if (!latest_reply_id) return of(undefined);
          else
            return this.db
              .object<ReplyItem>(`reply/${survey_id}/${latest_reply_id}`)
              .valueChanges()
              .pipe(
                first(),
                map(reply => {
                  this.data = reply;
                  return { [reply.as]: reply.snapshot };
                })
              );
        }),
        tap(data => this.tracker.load(data))
      );
  }
  save(
    survey: Survey,
    as: string = this.auth.as,
    replierInfo: {
      disaplyName?: string;
      phoneNumber?: string;
      email?: string;
    } = {}
  ) {
    return Promise.all(
      flatten(survey.questions.map(q => q.options)).map(o =>
        this.confirm(o, as)
      )
    )
      .then(list => merge({}, ...list))
      .then(snapshot => {
        const survey_id = survey.id;
        const survey_version = survey.version;
        console.log('saving a reply...');
        return firebase.functions().httpsCallable('replyInsert')({
          ...replierInfo,
          survey_id,
          survey_version,
          as,
          snapshot
        });
      })
      .then(() => {
        return this.tracker.load(this.tracker.value);
      });
  }
  private wailUntilCheckProcessFinished(
    option: Option,
    as: string
  ): Observable<any> {
    const checkType_o: CheckTypes = get(this.getCheck(option, as), 'type');
    if (
      typeof checkType_o === 'string' && checkType_o.match(/ing$/)
        ? true
        : false
    ) {
      console.log(
        `wait until ${option.text}'s ${checkType_o} status is resolved.`
      );
      const tracker = this.tracker.getChildTracker([
        as,
        option.question.id,
        option.id
      ]);
      return tracker.onContentUpdate.pipe(
        timeout(5 * 1000),
        map(e => get(e.to, 'type')),
        takeWhileInclusive(
          checkType_n =>
            typeof checkType_n === 'string' && checkType_n.match(/ing$/)
              ? true
              : false
        ),
        last()
      );
    } else {
      return of(null);
    }
  }
  private confirm(option: Option, as: string): Promise<any> {
    return this.wailUntilCheckProcessFinished(option, as)
      .toPromise()
      .then(() => {
        const checkType_o: CheckTypes = get(this.getCheck(option, as), 'type');
        switch (checkType_o) {
          case 'reserved':
            return this.transaction
              .fromAToB(
                this.transaction.getCheckRef(option, as).child('type'),
                'reserved',
                'confirmed'
              )
              .then(() => this.snapshot(option, as));
          case 'confirmed-to-be-canceled':
            return this.transaction
              .fromAToB(
                this.transaction.getCheckRef(option, as).child('type'),
                'confirmed-to-be-canceled',
                'unconfirming'
              )
              .then(() => null);
          case 'static':
          case 'confirmed':
            return Promise.resolve(this.snapshot(option, as));
          case undefined:
            return Promise.resolve(null);
          default:
            throw new Error(`unsuported checktype ${checkType_o}`);
        }
      });
  }
  private snapshot(option: Option, as: string): SurveyLevelAnswer {
    const q_id = option.question.id;
    const o_id = option.id;
    
    const check = this.getCheck(option, as);
    const order:number = check && option.question.options.map(option=>get(this.getCheck(option, as), 'timestamp')).filter(v=>v).sort().indexOf(get(check, 'timestamp'));
    return { [q_id]: { [o_id]: {...check, order }}};
  }
  private uncheck(option: Option, as: string): Observable<ToggleEvent> {
    return this.wailUntilCheckProcessFinished(option, as).pipe(
      switchMap(() => {
        const checkType_o: CheckTypes = get(this.getCheck(option, as), 'type');
        const v_o = this.getCheck(option, as);
        return this.queue.add(
          option.id,
          of(null)
            .pipe(
              switchMap(() => {
                switch (checkType_o) {
                  case 'reserved':
                    this.setCheck(option, null, as);
                    return this.transaction
                      .fromAToB(
                        this.transaction.getCheckRef(option, as).child('type'),
                        'reserved',
                        'unreserving'
                      )
                      .catch(e => {
                        this.setCheck(option, v_o, as);
                        return Promise.reject(e);
                      });
                  case 'confirmed':
                    this.updateCheck(
                      option,
                      'type',
                      'confirmed-to-be-canceled',
                      as
                    );
                    return this.transaction
                      .fromAToB(
                        this.transaction.getCheckRef(option, as).child('type'),
                        'confirmed',
                        'confirmed-to-be-canceled'
                      )
                      .catch(e => {
                        this.updateCheck(option, 'type', 'confirmed', as);
                        return Promise.reject(e);
                      });
                  case 'static':
                    this.setCheck(option, null, as);
                    return Promise.resolve(null);
                  case 'confirmed-to-be-canceled':
                  case 'unreserving': // already unreserving process in prgress!!
                    return Promise.resolve(null);
                  default:
                    return Promise.reject(
                      new Error(
                        `cannot understand old checkType : ${checkType_o}`
                      )
                    );
                }
              }),
              map(() => {
                return {
                  type: ToggleEventTypes.ToggleSucceeded,
                  option: option,
                  checking: false
                };
              }),
              tap(
                e => console.log(e.type, e.option.text, e.checking),
                e => console.error(e)
              ),
              catchError(() =>
                of({
                  type: ToggleEventTypes.ToggleFailed,
                  option: option,
                  checking: false
                })
              )
            )
            .pipe(
              startWith({
                type: ToggleEventTypes.ToggleStarted,
                option: option,
                checking: false
              })
            )
        );
      })
    );
  }
  private check(
    option: Option,
    value: any,
    as: string
  ): Observable<ToggleEvent> {
    return this.wailUntilCheckProcessFinished(option, as).pipe(
      switchMap(() => {
        const transaction: boolean =
          typeof option.quota === 'number' && option.quota > 0;
        const checkType_o: CheckTypes = get(this.getCheck(option, as), 'type');
        const v_o = this.getCheck(option, as) || null;
        let checkType_n: CheckTypes;
        switch (checkType_o) {
          case undefined:
            checkType_n = transaction ? 'reserving' : 'static';
            break;
          case 'confirmed-to-be-canceled':
            checkType_n = 'confirmed';
            break;
          case 'reserving': // already checking
          case 'reserved': // already checked
          case 'confirmed': // already checked
            return of(null);
          case 'static':
            checkType_n = 'static';
            break;
          case 'unreserving':
            checkType_n = 'reserving';
            break;
          default:
            throw new Error(`cannot understand old checkType : ${checkType_o}`);
        }

        const v_n:Check = {
          value,
          as,
          type: checkType_n,
          id: this.db.createPushId(),
          timestamp:Date.now(),
        };
        return this.queue.add(
          option.id,
          of(null)
            .pipe(
              switchMap(() => {
                switch (`${checkType_o}->${checkType_n}`) {
                  case 'undefined->reserving':
                    this.setCheck(option, v_n, as);
                    return this.transaction
                      .set(this.transaction.getCheckRef(option, as), v_n)
                      .catch(e => {
                        this.setCheck(option, v_o, as);
                        return Promise.reject(e);
                      });
                  case 'undefined->static':
                  case 'static->static':
                    this.setCheck(option, v_n, as);
                    return Promise.resolve(null);
                  case 'confirmed-to-be-canceled->confirmed':
                    this.updateCheck(option, 'type', 'confirmed', as);
                    return this.transaction
                      .fromAToB(
                        this.transaction.getCheckRef(option, as).child('type'),
                        'confirmed-to-be-canceled',
                        'confirmed'
                      )
                      .catch(e => {
                        this.updateCheck(
                          option,
                          'type',
                          'confirmed-to-be-canceled',
                          as
                        );
                        return Promise.reject(e);
                      });
                  default:
                    throw new Error(
                      `cannot understand checkingType : ${checkType_o}->${checkType_n}`
                    );
                }
              }),
              //  startWith({type:ToggleEventTypes.ToggleStarted, option:option, checking:true}),
              map(() => {
                return {
                  type: ToggleEventTypes.ToggleSucceeded,
                  option: option,
                  checking: true
                };
              }),
              tap(
                e => console.log(e.type, e.option.text, e.checking),
                e => console.error(e)
              ),
              catchError(() =>
                of({
                  type: ToggleEventTypes.ToggleFailed,
                  option: option,
                  checking: true
                })
              )
            )
            .pipe(
              startWith({
                type: ToggleEventTypes.ToggleStarted,
                option: option,
                checking: true
              })
            )
        );
      })
    );
  }
  private handleMaxCheckedOptionCnt(
    option: Option,
    as: string
  ): Observable<any> {
    const max_checked_option_cnt = option.question.max_checked_option_cnt,
      checked_option_cnt = option.question.options.filter(o =>
        this.isChecked(o, undefined, as)
      ).length;
    if (
      typeof max_checked_option_cnt !== 'number' ||
      max_checked_option_cnt > checked_option_cnt ||
      checked_option_cnt === 0
    ) {
      return of(null);
    } else if (max_checked_option_cnt === 1) {
      let checked = option.question.options.filter(
        o => o.id !== option.id && this.getCheck(o, as)
      );
      // return forkJoin(checked.map(o=>this.queue.add(o.id, this.toggle(o, null, as))))
      return forkJoin(checked.map(o => this.uncheck(o, as)));
    } else {
      throw new Error('you have reached the max_checked_option_cnt!');
    }
  }
  setCheck(option: Option, check: Check, as: string): void {
    this.tracker.set([as, option.question.id, option.id], check);
  }
  updateCheck(option: Option, path: string, value: any, as: string): void {
    this.tracker.set([as, option.question.id, option.id, path], value);
  }
  getCheck(option: Option, as: string): Check {
    return this.tracker.get([as, option.question.id, option.id]);
  }
  isChecked(
    option: Option,
    checkedTypes: string[] = ['confirmed', 'reserved', 'reserving', 'static'],
    as:string = this.auth.as,
  ): boolean {
    let check: Check = this.getCheck(option, as);
    return check && checkedTypes.includes(check.type) ? true : false;
  }
}
